// src/components/viewer/RoomSearchResultsBanner.tsx
import React, { useMemo } from "react";
import { ActionIcon, Alert, Group, Text } from "@mantine/core";
import { FaInfoCircle } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { selectMcbimSpaceHitSetByFreeTextSearch } from "./ViewerRedux/viewerSelectors";
import {
  selectActiveSearch,
  selectRoomSearchText,
  clearFreeTextSearchInRedux,
} from "./ViewerRedux/viewerSlice";

export const RoomSearchResultsBanner: React.FC = () => {
  const dispatch = useAppDispatch();

  const activeSearch = useAppSelector(selectActiveSearch);
  const query = useAppSelector(selectRoomSearchText);
  const hitSet = useAppSelector(selectMcbimSpaceHitSetByFreeTextSearch);

  const q = (query ?? "").trim();
  const isActive = activeSearch === "ROOM" && q.length > 0;

  const count = hitSet?.size ?? 0;

  const title = useMemo(() => {
    if (!isActive) return "";
    if (hitSet == null) return "";
    if (count === 0) return "Ingen rom matcher søket";
    if (count === 1) return "1 rom matcher søket";
    return `${count} rom matcher søket`;
  }, [isActive, hitSet, count]);

  if (!isActive) return null;
  if (hitSet == null) return null; // samme semantikk som hitSet-selectoren din

  return (
    <Alert
      variant="light"
      color={count === 0 ? "gray" : "blue"}
      radius="xl"
      icon={<FaInfoCircle size={16} />}
      styles={{
        root: {
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 14,
          paddingRight: 10,
          backgroundColor:
            count === 0 ? "var(--mantine-color-gray-1)" : "var(--mantine-color-blue-1)",
          border:
            count === 0
              ? "1px solid var(--mantine-color-gray-4)"
              : "1px solid var(--mantine-color-blue-4)",
          boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <div>
          <Text size="sm" fw={600} c="black">
            {title}
          </Text>
          <Text size="xs" c="dimmed">
            Søkeord:{" "}
            <Text span fw={600}>
              {q}
            </Text>
          </Text>
        </div>

        <ActionIcon
          variant="subtle"
          color={count === 0 ? "gray" : "blue"}
          radius="xl"
          aria-label="Tøm søk"
          onClick={() => dispatch(clearFreeTextSearchInRedux())}
        >
          <FaXmark size={14} />
        </ActionIcon>
      </Group>
    </Alert>
  );
};
